import React, { useEffect, useState } from "react";
import { IPhotoInputComponent, SWTypes } from "interfaces/sw/SWInterfaces";
import { useDispatch } from "react-redux";
import useSelector from "store/useSelector";
import { updatePhotoInput } from "store/manageSW/manageSWActions";
import "./ComponentEditor.scss";
import { showInfoToast } from "store/toast/toastActions";
import { copyToClipboard } from "utilities/clipboard";
import MasterDataInput from "../../attributes/MasterDataInput";
import { IMasterDataItem } from "interfaces/masterData/masterDataInterfaces";
import { loadMviModels } from "store/masterData/masterDataActions";

interface IPhotoInputEditorProps {
  photoInput: IPhotoInputComponent,
  allowEdit: boolean,
  stepGuid: string,
  labelText: string,
}

const PhotoInputEditor: React.FC<IPhotoInputEditorProps> = ({
  photoInput,
  allowEdit,
  stepGuid,
  labelText,
}) => {
  const {
    manageSW: {
      SW,
      mfgDisableVisionInspection,
      mviEnabled,
    },
    approvals: {
      currentApproval
    },
    masterData: {
      mviModels,
    }
  } = useSelector((store) => store);
  const sw = currentApproval?.sw
    ? currentApproval.sw
    : SW;
  const dispatch = useDispatch();
  const [isVisionInspectionEnabled, setIsVisionInspectionEnabled] = useState(false);

  useEffect(() => {
    if (sw.type === SWTypes.MFGRC || sw.type === SWTypes.MFGSWI) {
      if (photoInput) {
        const data = photoInput.data
          ? JSON.parse(photoInput.data)
          : { IsVisionInspectionEnabled: false };

        if (!photoInput.data) {
          const isVisionInspectionEnabled = JSON.stringify(data);
          dispatch(
            updatePhotoInput({
              stepGuid,
              component: {
                ...photoInput,
                data: isVisionInspectionEnabled,
              },
            })
          );
        }

        setIsVisionInspectionEnabled(data.IsVisionInspectionEnabled);
      }
    }

    if (sw.type === SWTypes.TLMSWI || sw.type === SWTypes.TLMRC) {
      dispatch(loadMviModels({ onlyActive: true }));
    }
  }, [sw, stepGuid, photoInput, dispatch]);

  const handleCheckboxChange = (e: any) => {
    setIsVisionInspectionEnabled(e.target.checked);
    const updatedData = JSON.stringify({ IsVisionInspectionEnabled: e.target.checked });
    dispatch(updatePhotoInput({
      stepGuid,
      component: {
        ...photoInput,
        data: updatedData,
      },
    }));
  };

  const copyComponentGuidToClipboard = () => {
    copyToClipboard({
      value: photoInput.immutableGuid ?? "",
      errorMessage: "Unable to copy Component GUID",
    })
    dispatch(showInfoToast("Component Guid copied!"));
  }

  const copyGuidButton = (
    <div className={`container-toolTip ${photoInput.immutableGuid ? "" : "disabled"}`}>
      <button className={`primary-button ${photoInput.immutableGuid ? "" : "disabled"}`} onClick={() => {
        copyComponentGuidToClipboard()
      }}>Copy Component GUID</button>
      <div className="toolTip-text">Copy Component Guid button will be enabled after saving the standard work.</div>
    </div>
  );

  const handleMviModel = (guid: string | undefined) => {
    const updatedData = JSON.stringify({ mviModel: guid });
    dispatch(updatePhotoInput({
      stepGuid,
      component: {
        ...photoInput,
        data: updatedData,
      },
    }));
  }

  const renderMviModels = () => {
    let items: IMasterDataItem[] = [];
    const data = photoInput.data
      ? JSON.parse(photoInput.data)
      : undefined;

    if (data) {
      items = mviModels.items.filter(x => x.guid === data.mviModel);
    }

    return (
      <MasterDataInput
        label={"MVI Model"}
        isMandatory={false}
        disabled={!allowEdit}
        selectedItems={items}
        allowMultiple={false}
        onAddItem={(item: IMasterDataItem) =>
          handleMviModel(item.guid)
        }
        onRemoveItem={() =>
          handleMviModel(undefined)
        }
        loadItems={() => dispatch(loadMviModels({ onlyActive: true }))}
        masterDataSection={mviModels}
        itemFormatter={(item: IMasterDataItem) => item.value}
        helpText={"Maximo Visual Inspection model for automatic image analysis"}
      />
    );
  };

  return (
    <>
      <div className="component-editor">
        <label className="mandatory block">{labelText}</label>
        {allowEdit &&
          <input
            type="text"
            defaultValue={photoInput.label}
            onBlur={(e) => dispatch(updatePhotoInput({
              stepGuid,
              component: {
                ...photoInput,
                label: e.target.value,
              },
            }))}
          />
        }
        {!allowEdit &&
          <span>{photoInput.label}</span>
        }
      </div>
      {(sw.type === SWTypes.MFGSWI && !mfgDisableVisionInspection) && (
        <div className="component-editor">
          <div className="grid-container">
            <div className="grid-item flex-column">
              <label>
                Vision Inspection Enabled
                <input
                  type="checkbox"
                  disabled={!allowEdit}
                  className="conditional-check"
                  checked={isVisionInspectionEnabled}
                  onChange={handleCheckboxChange}
                />
              </label>
              {copyGuidButton}
            </div>
            {!currentApproval?.sw
              && <div className="grid-item">
                <span className="italic-text">
                  Note: the default setting for vision inspection is single-category annotation. For multiple-categories annotation, the manufacturing engineer must adjust the settings in the ML Vision Inspection web apps.
                </span>
              </div>
            }
          </div>
        </div>
      )}

      {(sw.type === SWTypes.TLMSWI || sw.type === SWTypes.TLMRC) && mviEnabled &&
        renderMviModels()
      }
    </>
  );
}

export default PhotoInputEditor;